import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Users2, FolderKanban, Ticket, Settings, Boxes, Newspaper,
  Sparkles, Zap, Target, LogOut, Home, Menu, Image, Star, HelpCircle, Package, Layers, FileText,
} from "lucide-react";
import Logo from "@/components/brand/Logo";
import ThemeToggle from "@/components/site/ThemeToggle";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { useAuth } from "@/context/AuthContext";

const GROUPS = [
  { title: "Overview", links: [
    { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
    { to: "/admin/opportunities", label: "Opportunities", icon: Target },
  ]},
  { title: "CRM & Sales", links: [
    { to: "/admin/leads", label: "Leads / CRM", icon: Users2 },
    { to: "/admin/proposals", label: "Proposals", icon: FileText },
    { to: "/admin/ad-pricing", label: "Ad Pricing", icon: Package },
    { to: "/admin/projects", label: "Projects", icon: FolderKanban },
    { to: "/admin/tickets", label: "Support", icon: Ticket },
  ]},
  { title: "Content", links: [
    { to: "/admin/services", label: "Services", icon: Boxes },
    { to: "/admin/packages", label: "Packages", icon: Package },
    { to: "/admin/portfolio", label: "Portfolio", icon: Image },
    { to: "/admin/blog", label: "Blog", icon: Newspaper },
    { to: "/admin/testimonials", label: "Testimonials", icon: Star },
    { to: "/admin/faqs", label: "FAQs", icon: HelpCircle },
  ]},
  { title: "System", links: [
    { to: "/admin/automations", label: "Automation", icon: Zap },
    { to: "/admin/users", label: "Users & Roles", icon: Layers },
    { to: "/admin/settings", label: "Settings", icon: Settings },
  ]},
];

function SidebarContent({ onNav }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const doLogout = async () => { await logout(); navigate("/"); };
  return (
    <div className="flex h-full flex-col">
      <Logo className="px-2 py-2" />
      <div className="mt-1 px-2 text-[11px] font-semibold uppercase tracking-widest text-primary">Owner Control Center</div>
      <nav className="mt-4 flex-1 space-y-5 overflow-y-auto pb-4">
        {GROUPS.map((g) => (
          <div key={g.title}>
            <p className="px-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{g.title}</p>
            <div className="mt-1.5 space-y-0.5">
              {g.links.map((l) => (
                <NavLink key={l.to} to={l.to} end={l.end} onClick={onNav} data-testid={`admin-nav-${l.label.split(" ")[0].toLowerCase()}`}
                  className={({ isActive }) => `flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${isActive ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary hover:text-foreground"}`}>
                  <l.icon className="h-4 w-4" /> {l.label}
                </NavLink>
              ))}
            </div>
          </div>
        ))}
      </nav>
      <div className="space-y-1 border-t border-border pt-3">
        <p className="px-3 text-xs text-muted-foreground">{user?.email}</p>
        <NavLink to="/" onClick={onNav} className="flex items-center gap-3 rounded-lg px-3 py-2 text-sm text-muted-foreground hover:bg-secondary"><Home className="h-4 w-4" /> View site</NavLink>
        <button onClick={doLogout} className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm text-muted-foreground hover:bg-secondary" data-testid="admin-logout"><LogOut className="h-4 w-4" /> Log out</button>
      </div>
    </div>
  );
}

export default function AdminLayout() {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  useEffect(() => { const el = document.getElementById("admin-main"); if (el) el.scrollTop = 0; }, [pathname]);

  return (
    <div className="flex min-h-screen bg-background">
      <aside className="sticky top-0 hidden h-screen w-64 shrink-0 border-r border-border bg-card/40 p-4 lg:block">
        <SidebarContent />
      </aside>
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 flex h-16 items-center justify-between border-b border-border bs-glass px-5">
          <div className="flex items-center gap-3">
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild><Button variant="outline" size="icon" className="lg:hidden" data-testid="admin-mobile-menu"><Menu className="h-5 w-5" /></Button></SheetTrigger>
              <SheetContent side="left" className="w-72 p-4"><SidebarContent onNav={() => setOpen(false)} /></SheetContent>
            </Sheet>
            <span className="flex items-center gap-2 text-sm font-semibold"><Sparkles className="h-4 w-4 text-primary" /> Admin</span>
          </div>
          <ThemeToggle />
        </header>
        <main id="admin-main" className="flex-1 overflow-y-auto p-5 sm:p-7">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
